import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { Fragment, useState, ReactNode, useMemo } from 'react';
import { IoIosArrowForward } from 'react-icons/io';

import { BottomSheet } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useModalSize from '@/hooks/useModalSize';
import { Size, ModalActions } from '@/types';
import { cn, scrollSmooth } from '@/utils';

type CommonProps = {
  title?: string | ReactNode;
  className?: string;
  size?: Size;
  showCloseButton?: boolean;
  closeOutside?: boolean;
  disableDrag?: boolean;
  onAfterClose?: () => void;
  bottomSheetClassName?: string;
  headerSheetClassName?: string;
  titleClassName?: string;
  disableBodyScroll?: boolean;
  withBack?: boolean;
};

type DefaultProps = {
  variant: 'default';
  trigger: ({ openModal, isOpen }: ModalActions) => ReactNode;
  render: ({ openModal, isOpen, closeModal }: ModalActions) => ReactNode;
};

type ControlledProps = {
  variant: 'controlled';
  isControlledOpen: boolean;
  controlledOnClose: () => void;
  render: () => ReactNode;
};

type Props = (CommonProps & DefaultProps) | (CommonProps & ControlledProps);

const Modal = (props: Props) => {
  const {
    render,
    className,
    size = 'md',
    title,
    withBack,
    variant,
    showCloseButton = true,
    closeOutside = true,
    disableDrag,
    bottomSheetClassName,
    titleClassName,
    disableBodyScroll,
    headerSheetClassName,
    onAfterClose,
  } = props;

  const { isDesktop } = useIsDesktop();
  const [isOpen, setIsOpen] = useState(false);

  const closeModal = () => {
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
    scrollSmooth();
  };

  const modalSizeClass = useModalSize(size);

  const contentClass = cn(
    'max-h-[90vh] px-16 py-14 inline-block w-full overflow-hidden text-left align-middle transition-all transform bg-white border border-black border-opacity-5 shadow-xl rounded-[11px] relative flex flex-col',
    className,
    modalSizeClass
  );

  const isDefaultVariant = variant === 'default';
  const isControlledOpen = variant === 'controlled';

  const modalIsOpen = useMemo(() => {
    if ('isControlledOpen' in props) {
      return props.isControlledOpen;
    }

    if (isDefaultVariant) {
      return isOpen;
    }

    return isOpen;
  }, [isDefaultVariant, isOpen, props]);

  const handleClose = (canClose: boolean) => {
    if (!canClose) return;

    if (isControlledOpen) {
      return props.controlledOnClose();
    }

    return closeModal();
  };

  const handleCloseOutside = () => handleClose(closeOutside);
  const handleCloseButton = () => handleClose(showCloseButton);

  if (isDesktop) {
    return (
      <>
        {isDefaultVariant &&
          props.trigger?.({ openModal, isOpen: modalIsOpen, closeModal })}
        <Dialog
          transition
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto focus:outline-none transition duration-75 ease-in-out data-[closed]:opacity-0"
          open={modalIsOpen}
          onClose={handleCloseOutside}
        >
          {/* The backdrop, rendered as a fixed sibling to the panel container */}
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-neutral-900 bg-opacity-50 transition duration-75 ease-in-out data-[closed]:opacity-0"
            onClick={handleCloseOutside}
          />
          {/* Full-screen container to center the panel */}
          <div
            className={cn(
              'fixed inset-0 flex items-center justify-center p-4 min-h-screen px-1 text-center md:px-4',
              `max-width-[${modalSizeClass}]`
            )}
          >
            {/* The actual dialog panel  */}
            <DialogPanel
              transition
              className={cn(
                'transition duration-75 ease-in-out data-[closed]:scale-95 scale-100',
                contentClass
              )}
            >
              <div>
                {withBack && (
                  <button onClick={handleCloseButton}>
                    <IoIosArrowForward className="rotate-180 absolute h-8 w-8 text-gdt-gray top-3 left-5" />
                  </button>
                )}
                {title && (
                  <DialogTitle
                    as="h3"
                    className={cn(
                      {
                        'text-gdt-gray lg:text-black font-bold text-base text-center':
                          title,
                      },
                      titleClassName
                    )}
                  >
                    {title}
                  </DialogTitle>
                )}
                {showCloseButton && (
                  <button
                    className={`absolute z-[1] right-4 top-[13px] text-gdt-gray-4`}
                    data-testid="close-modal"
                    type="button"
                    onClick={handleCloseButton}
                  >
                    <svg
                      fill="none"
                      height="27"
                      viewBox="0 0 27 27"
                      width="27"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        clipRule="evenodd"
                        d="M21.0455 5.9545C21.4848 6.39384 21.4848 7.10616 21.0455 7.5455L7.5455 21.0455C7.10616 21.4848 6.39384 21.4848 5.9545 21.0455C5.51517 20.6062 5.51517 19.8938 5.9545 19.4545L19.4545 5.9545C19.8938 5.51517 20.6062 5.51517 21.0455 5.9545Z"
                        fill="#A09EB3"
                        fillRule="evenodd"
                      />
                      <path
                        clipRule="evenodd"
                        d="M5.9545 5.9545C6.39384 5.51517 7.10616 5.51517 7.5455 5.9545L21.0455 19.4545C21.4848 19.8938 21.4848 20.6062 21.0455 21.0455C20.6062 21.4848 19.8938 21.4848 19.4545 21.0455L5.9545 7.5455C5.51517 7.10616 5.51517 6.39384 5.9545 5.9545Z"
                        fill="#A09EB3"
                        fillRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
              {render({ openModal, isOpen: modalIsOpen, closeModal })}
            </DialogPanel>
          </div>
        </Dialog>
      </>
    );
  }

  return (
    <Fragment>
      {isDefaultVariant &&
        props.trigger?.({ openModal, isOpen: modalIsOpen, closeModal })}
      <BottomSheet
        closeOutside={closeOutside}
        disableBodyScroll={disableBodyScroll}
        disableDrag={disableDrag}
        headerClass={headerSheetClassName}
        isOpen={modalIsOpen}
        showCloseButton={showCloseButton}
        title={title}
        wrapperClassName={bottomSheetClassName}
        onAfterClose={onAfterClose}
        onClose={handleCloseButton}
      >
        {render({ openModal, isOpen, closeModal })}
      </BottomSheet>
    </Fragment>
  );
};

export default Modal;
